"use client";

import React, { useEffect, useState } from "react";
import { get } from "@/helpers/api.helper";
import { FiImage } from "react-icons/fi";
import styles from "./EventBannerImage.module.css";
interface EventBannerImageProps {
  imageUrl?: string | null;
  title: string;
  className?: string;
}

const detectImageContentType = (value: string): string => {
  if (value.startsWith("/9j/")) return "image/jpeg";
  if (value.startsWith("iVBOR")) return "image/png";
  if (value.startsWith("R0lGOD")) return "image/gif";
  if (value.startsWith("UklGR")) return "image/webp";

  return "image/jpeg";
};

const imagePromiseCache = new Map<string, Promise<string>>();

const EventBannerImage: React.FC<EventBannerImageProps> = ({ imageUrl, title, className }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const finalUrl = imageUrl?.trim() || "";

    if (!finalUrl) {
      setSrc(null);
      setLoading(false);
      return;
    }

    if (finalUrl.startsWith("data:") || finalUrl.startsWith("blob:")) {
      setSrc(finalUrl);
      setLoading(false);
      return;
    }

    setLoading(true);
    let isMounted = true;
    let promise = imagePromiseCache.get(finalUrl);
    
    if (!promise) {
      promise = get<any>(finalUrl)
        .then((data) => {
          if (data?.imageBase64) {
            return `data:${detectImageContentType(data.imageBase64)};base64,${data.imageBase64}`;
          }
          return finalUrl;
        })
        .catch(() => finalUrl);
        
      imagePromiseCache.set(finalUrl, promise);
    }

    promise.then((resolvedSrc) => {
      if (isMounted) setSrc(resolvedSrc);
    }).finally(() => {
      if (isMounted) setLoading(false);
    });
    
    return () => {
      isMounted = false;
    };
  }, [imageUrl]);

  if (loading) {
    return (
      <div
        className={`placeholder-glow ${className || ""} ${styles.bannerImage}`}
      >
        <span className={`placeholder w-100 h-100 d-block ${styles.placeholderSpan}`}></span>
      </div>
    );
  }

  if (!src || error) {
    return (
      <div
        className={`${className || ""} ${styles.fallbackContainer}`.trim()}
      >
        <FiImage size={40} className={styles.fallbackIcon} />
        <span className={styles.fallbackText}>No Image Available</span>
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={title} 
      className={`${className || ""} ${styles.bannerImage} ${error ? styles.hidden : ""}`.trim()} 
      loading="lazy" 
      onError={() => setError(true)}
    />
  );
};

export default EventBannerImage;
